import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Dumbbell, ArrowLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ToolCard from '@/components/ToolCard';
import SponsorSection from '@/components/SponsorSection';
import SportsFilter from '@/components/SportsFilter';
import PlatformFilter from '@/components/PlatformFilter';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Tool } from '@/data/tools';
import MetaTags from '@/components/MetaTags';
import ShareButton from '@/components/ShareButton';

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [filteredTools, setFilteredTools] = useState<Tool[]>([]);
  const [allTools, setAllTools] = useState<Tool[]>([]);
  const [category, setCategory] = useState<string>('');
  const [categorySeoDescription, setCategorySeoDescription] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [sponsors, setSponsors] = useState<{
    gold: Tool | null;
    silver: Tool | null;
    bronze: Tool | null;
  }>({ gold: null, silver: null, bronze: null });
  
  // Add states for filters
  const [sports, setSports] = useState<{ id: string, name: string }[]>([]);
  const [platforms, setPlatforms] = useState<{ id: string, name: string }[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<string | null>(null);
  const [selectedPlatformId, setSelectedPlatformId] = useState<string | null>(null);

  // Helper function to normalize URL slug for database comparison
  const normalizeSlug = (urlSlug: string): string => {
    // Replace encoded characters with their actual representation for database comparison
    return urlSlug
      .replace(/%26/g, '&')  // Replace %26 with &
      .replace(/-and-/g, '-&-'); // Also try with 'and' in case it was converted
  };

  useEffect(() => {
    const fetchCategoryData = async () => {
      setLoading(true);

      try {
        // Normalize the slug for proper database comparison
        const normalizedSlug = normalizeSlug(slug || '');
        
        console.log(`Fetching category with normalized slug: ${normalizedSlug}`);
        
        // Use "like" or "ilike" operator instead of "eq" for more flexible matching
        const { data: categoryData, error: categoryError } = await supabase
          .from('categories')
          .select(`
            id, 
            name,
            page_title,
            seo_description,
            gold_sponsor_app_id,
            silver_sponsor_app_id,
            bronze_sponsor_app_id
          `)
          .or(`url_slug.eq.${normalizedSlug},url_slug.eq.${slug}`)
          .maybeSingle();

        if (categoryError) throw categoryError;
        
        if (categoryData) {
          setCategory(categoryData.name);
          setCategorySeoDescription(categoryData.seo_description || '');
          
          // Fetch sponsor apps if they exist
          const sponsorIds = [
            categoryData.gold_sponsor_app_id,
            categoryData.silver_sponsor_app_id,
            categoryData.bronze_sponsor_app_id
          ].filter(Boolean);

          if (sponsorIds.length > 0) {
            const { data: sponsorApps, error: sponsorError } = await supabase
              .from('apps')
              .select(`
                id,
                name,
                description,
                visit_url,
                url_slug,
                approved,
                featured,
                logo_url,
                app_platforms(platform_id, platforms(name)),
                app_sports(sport_id, sports(name))
              `)
              .in('id', sponsorIds)
              .eq('approved', true);

            if (sponsorError) throw sponsorError;

            if (sponsorApps) {
              const transformedSponsors = sponsorApps.map(app => ({
                id: app.id,
                name: app.name,
                logo: app.logo_url || "https://placehold.co/60x60",
                rating: 4.5,
                reviews: 0,
                bookmarks: 0,
                pricing: "Free Trial",
                description: app.description,
                tags: [],
                category: categoryData.name,
                sports: Array.from(new Set(app.app_sports.map((s: any) => s.sports?.name).filter(Boolean))),
                platforms: Array.from(new Set(app.app_platforms.map((p: any) => p.platforms?.name).filter(Boolean))),
                featured: app.featured,
                visitUrl: app.visit_url,
                url_slug: app.url_slug,
              }));

              setSponsors({
                gold: transformedSponsors.find(s => s.id === categoryData.gold_sponsor_app_id) || null,
                silver: transformedSponsors.find(s => s.id === categoryData.silver_sponsor_app_id) || null,
                bronze: transformedSponsors.find(s => s.id === categoryData.bronze_sponsor_app_id) || null,
              });
            }
          }

          // Then get all regular apps for this category
          const { data: appsData, error: appsError } = await supabase
            .from('apps')
            .select(`
              id,
              name,
              description,
              visit_url,
              url_slug,
              approved,
              featured,
              logo_url,
              app_categories!inner(category_id),
              app_platforms(platform_id, platforms(name)),
              app_sports(sport_id, sports(name))
            `)
            .eq('approved', true)
            .eq('app_categories.category_id', categoryData.id);

          if (appsError) throw appsError;
          
          if (appsData) {
            // Transform the data into the Tool format
            const transformedTools: Tool[] = appsData.map(app => {
              // Extract unique platforms and sports
              const platforms = Array.from(new Set(
                app.app_platforms.map((p: any) => p.platforms?.name).filter(Boolean)
              ));
              
              const sports = Array.from(new Set(
                app.app_sports.map((s: any) => s.sports?.name).filter(Boolean)
              ));
              
              return {
                id: app.id,
                name: app.name,
                logo: app.logo_url || "https://placehold.co/60x60",
                rating: 4.5, // Default rating
                reviews: 0, // Default reviews count
                bookmarks: 0, // Default bookmarks count
                pricing: "Free Trial", // Default pricing
                description: app.description,
                tags: [], // Could be derived from categories
                category: categoryData.name, // We know it has this category
                sports: sports,
                platforms: platforms,
                featured: app.featured,
                visitUrl: app.visit_url,
                url_slug: app.url_slug,
              };
            });

            setAllTools(transformedTools);
            setFilteredTools(transformedTools);
          }
          
          // Fetch all sports
          const { data: sportsData, error: sportsError } = await supabase
            .from('sports')
            .select('id, name')
            .order('name');
            
          if (sportsError) throw sportsError;
          if (sportsData) {
            setSports(sportsData);
          }
          
          // Fetch all platforms
          const { data: platformsData, error: platformsError } = await supabase
            .from('platforms')
            .select('id, name')
            .order('name');
            
          if (platformsError) throw platformsError;
          if (platformsData) {
            setPlatforms(platformsData);
          }
        } else {
          console.error('No category found with slug:', slug);
          toast({
            title: "Category not found",
            description: `We couldn't find a category with the name "${slug}".`,
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('Error fetching category data:', error);
        toast({
          title: "Error loading data",
          description: "There was a problem loading the category data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCategoryData();
    }
  }, [slug, toast]);
  
  // Handle sport selection
  const handleSportSelect = (sportId: string | null) => {
    setSelectedSportId(sportId);
  };
  
  // Handle platform selection
  const handlePlatformSelect = (platformId: string | null) => {
    setSelectedPlatformId(platformId);
  };
  
  // Apply filters effect
  useEffect(() => {
    if (allTools.length === 0) return;
    
    let filtered = [...allTools];
    
    // Filter by sport if selected
    if (selectedSportId) {
      const sportName = sports.find(s => s.id === selectedSportId)?.name;
      filtered = filtered.filter(tool => 
        tool.sports.some(s => s === sportName)
      );
    }
    
    // Filter by platform if selected
    if (selectedPlatformId) {
      const platformName = platforms.find(p => p.id === selectedPlatformId)?.name;
      filtered = filtered.filter(tool => 
        tool.platforms.some(p => p === platformName)
      );
    }
    
    setFilteredTools(filtered);
  }, [allTools, selectedSportId, selectedPlatformId, sports, platforms]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <MetaTags
        title={category ? `${category} Apps - FitnessAppSearch` : 'Loading...'}
        description={categorySeoDescription || (category ? `Discover the best ${category.toLowerCase()} apps and tools for your fitness journey.` : '')}
        canonicalUrl={`${window.location.origin}/category/${slug}`}
        imageUrl={`${window.location.origin}/og-image.png`}
      />
      <Navigation />
      <main className="container mx-auto px-4 py-8 flex-grow">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Dumbbell className="h-6 w-6 text-gray-600" />
              <h1 className="text-3xl font-bold">{category} Apps</h1>
            </div>
            <ShareButton title={`${category} Apps and Tools`} />
          </div>
          
          <p className="text-gray-600">
            Discover the best {category.toLowerCase()} apps and tools for your fitness journey.
          </p>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar with filters */}
            <div className="space-y-6">
              <SportsFilter 
                sports={sports} 
                selectedSportId={selectedSportId} 
                onSelectSport={handleSportSelect} 
              />
              <PlatformFilter 
                platforms={platforms} 
                selectedPlatformId={selectedPlatformId} 
                onSelectPlatform={handlePlatformSelect} 
              />
            </div>
            
            {/* Main content */}
            <div className="lg:col-span-3">
              <SponsorSection
                goldSponsor={sponsors.gold}
                silverSponsor={sponsors.silver}
                bronzeSponsor={sponsors.bronze}
              />
              
              {filteredTools.length > 0 ? (
                <>
                  <div className="mb-6 border-b pb-2">
                    <h2 className="text-2xl font-semibold">All {category} Tools</h2>
                  </div>
                  <div className="grid gap-4">
                    {filteredTools.map(tool => (
                      <ToolCard key={tool.id} tool={tool} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-lg text-gray-600">
                    No apps found with the selected filters. Try changing your filters.
                  </p>
                  <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">
                    Browse all apps
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CategoryPage;
